export const DEMO_PASSWORD = "service-demo"

export type DemoRole = "analyst" | "admin"

export type DemoAccount = {
  id: string
  email: string
  password: string
  name: string
  shortName: string
  role: DemoRole
  title: string
  agency: string
  initials: string
}

export type Viewer = Omit<DemoAccount, "password">

export type SceneStatus = "available" | "restricted" | "processing"
export type SceneType = "OPTICAL" | "SAR"

export type Scene = {
  id: string
  satellite: string
  type: SceneType
  location: string
  region: string
  capturedAt: string
  capturedLabel: string
  resolutionMeters: number
  cloudCover: number | null
  productLevel: string
  band: string
  polarization: string | null
  imagingMode: string
  fileSizeGb: number
  status: SceneStatus
  preview: string
  accent: "cyan" | "green" | "yellow" | "blue"
  center: [number, number]
  footprint: Array<[number, number]>
  description: string
}

export type OrderStatus =
  | "draft"
  | "submitted"
  | "review"
  | "approved"
  | "ready"
  | "completed"
  | "rejected"

export type OrderKind = "Archive" | "New tasking"

export type Order = {
  id: string
  kind: OrderKind
  title: string
  purpose: string
  areaLabel: string
  agency: string
  requester: string
  submittedAt: string
  submittedLabel: string
  updatedLabel: string
  status: OrderStatus
  sceneIds: Array<string>
  requestedSatellite: string
  dateRange: string
  notes: string
  operatorNote?: string
}

export type DownloadRecord = {
  id: string
  sceneId: string
  orderId: string
  fileName: string
  sizeGb: number
  requestedAt: string
  requestedLabel: string
  status: "available" | "preparing" | "expired"
  expiresLabel: string
}

export type Activity = {
  id: string
  kind: "order" | "download" | "catalogue" | "account"
  title: string
  detail: string
  timeLabel: string
  status?: OrderStatus | DownloadRecord["status"]
}

export type SupportTopic = {
  id: string
  question: string
  answer: string
  category: "Searching" | "Requests" | "Downloads" | "Account"
}

export type Agency = {
  id: string
  name: string
  code: string
  members: number
  openOrders: number
  allocationUsedPercent: number
  status: "Active" | "For review"
}

export type AdminOverview = {
  totals: {
    users: number
    agencies: number
    imagery: number
    openOrders: number
    readyDownloads: number
  }
  orderTrend: Array<{ label: string; value: number }>
  imageryBySatellite: Array<{ satellite: string; count: number }>
  agencies: Array<Agency>
  recentOrders: Array<Order>
}

export type CreateOrderInput = {
  kind: OrderKind
  title: string
  purpose: string
  areaLabel: string
  sceneIds: Array<string>
  requestedSatellite: string
  dateRange: string
  notes: string
}

export type SceneFilters = {
  query?: string
  type?: "Any" | SceneType
  satellite?: string
  maxCloudCover?: number
  maxResolution?: number
}

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  draft: "Draft",
  submitted: "Submitted",
  review: "Under review",
  approved: "Approved",
  ready: "Ready for download",
  completed: "Completed",
  rejected: "Needs revision",
}

export const ORDER_STATUS_TRANSITIONS: Record<
  OrderStatus,
  ReadonlyArray<Exclude<OrderStatus, "draft">>
> = {
  draft: ["submitted"],
  submitted: ["review", "rejected"],
  review: ["approved", "rejected"],
  approved: ["ready", "rejected"],
  ready: ["completed"],
  completed: [],
  rejected: ["review"],
}

export const SCENE_STATUS_LABELS: Record<SceneStatus, string> = {
  available: "Available",
  restricted: "By request",
  processing: "Processing",
}

export function viewerDefaultPath(viewer: Viewer | null) {
  return viewer?.role === "admin" ? "/admin" : "/portal"
}

export function formatFileSize(sizeGb: number) {
  return sizeGb < 1
    ? `${Math.round(sizeGb * 1024)} MB`
    : `${sizeGb.toFixed(1)} GB`
}

export function formatResolution(resolutionMeters: number) {
  return `${resolutionMeters.toFixed(resolutionMeters < 1 ? 2 : 1)} m`
}
